"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { usePathname, useSearchParams } from "next/navigation"
import Script from "next/script"
import {
  canUseMetaMarketing,
  clearMetaBrowserIdentifiers,
  getMetaBrowserSignals,
  getMetaMarketingConsent,
  setMetaMarketingConsent,
  type MetaMarketingConsent,
  type MetaBrowserSignals,
} from "@lib/analytics/meta-signals"
import {
  createBrowserEventId,
  normalizeBrowserEventId,
} from "@lib/analytics/event-id"
import { sendMetaAnalyticsCompanion } from "@lib/analytics/meta-companion-client"
import {
  processMetaClientParams,
  processMetaClientParamsWithStatus,
} from "@lib/analytics/meta-client-param-builder"
import {
  claimMetaPageViewLifecycle,
  resolveMetaPageViewLifecycle,
  type MetaPageViewLifecycle,
} from "@lib/analytics/meta-page-view-lifecycle"

type AnalyticsItem = {
  item_id: string
  item_name: string
  item_brand?: string
  item_category?: string
  item_variant?: string
  price?: number
  quantity?: number
  index?: number
}

type CommerceEventPayload = {
  items: AnalyticsItem[]
  currency?: string
  value?: number
  coupon?: string | null
  transactionId?: string
  contentType?: "product" | "product_group"
  paymentMethod?: string
}

type AnalyticsEventMeta = {
  eventId?: string
}

type MetaPixelUserData = {
  externalId?: string
  email?: string
  phone?: string
}

type MetaPixelEvent = {
  method: "track" | "trackCustom"
  name: string
  params?: Record<string, unknown>
  eventId?: string
}

type FbqFunction = {
  (...args: unknown[]): void
  callMethod?: (...args: unknown[]) => void
  queue?: unknown[][]
  push?: (...args: unknown[]) => void
  loaded?: boolean
  version?: string
  disablePushState?: boolean
}

type AnalyticsWindow = Window & {
  fbq?: FbqFunction
  _fbq?: FbqFunction
  dataLayer?: unknown[]
  gtag?: (...args: unknown[]) => void
  __metaPixelInitializedIds?: Record<string, true>
  __metaPageViewLifecycles?: Record<string, MetaPageViewLifecycle>
  __metaDeniedPageViewPaths?: Record<string, string>
  __gaConfiguredIds?: Record<string, true>
}

export const sanitizeMetaPixelId = (
  value?: string | null
): string | undefined => {
  if (typeof value !== "string") {
    return undefined
  }
  const trimmed = value.trim()
  if (!trimmed) {
    return undefined
  }
  const unwrapped = trimmed.replace(/^['"]+|['"]+$/g, "").trim()
  if (!unwrapped) {
    return undefined
  }
  if (!/^\d{5,20}$/.test(unwrapped)) {
    return undefined
  }
  return unwrapped
}

export const initializeMetaPixelOnce = ({
  fbq,
  pixelId,
  userData,
  initializedIds,
}: {
  fbq: FbqFunction
  pixelId?: string | null
  userData?: Record<string, string>
  initializedIds: Record<string, true>
}): boolean => {
  const sanitizedPixelId = sanitizeMetaPixelId(pixelId)
  if (!sanitizedPixelId || initializedIds[sanitizedPixelId]) return false
  if (userData && Object.keys(userData).length > 0) {
    fbq("init", sanitizedPixelId, userData)
  } else {
    fbq("init", sanitizedPixelId)
  }
  initializedIds[sanitizedPixelId] = true
  return true
}

const sanitizeCurrency = (currency?: string) => {
  if (typeof currency !== "string") return undefined
  const trimmed = currency.trim()
  return trimmed ? trimmed.toUpperCase() : undefined
}

const sanitizeAmount = (value?: number) => {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined
  return Number(value.toFixed(2))
}

const sanitizeEventId = (value?: string): string | undefined => {
  return normalizeBrowserEventId(value)
}

const sanitizeMetaUserDataString = (value?: string): string | undefined => {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

const sanitizeMetaUserDataEmail = (value?: string): string | undefined => {
  const normalized = sanitizeMetaUserDataString(value)
  if (!normalized) return undefined
  if (!normalized.includes("@")) return undefined
  return normalized.toLowerCase()
}

const sanitizeMetaUserDataPhone = (value?: string): string | undefined => {
  const normalized = sanitizeMetaUserDataString(value)
  if (!normalized) return undefined
  const digitsOnly = normalized.replace(/\D/g, "")
  return digitsOnly.length > 0 ? digitsOnly : undefined
}

export const buildMetaPixelAdvancedMatchingData = (
  userData?: MetaPixelUserData
): Record<string, string> => {
  const externalId = sanitizeMetaUserDataString(userData?.externalId)
  const email = sanitizeMetaUserDataEmail(userData?.email)
  const phone = sanitizeMetaUserDataPhone(userData?.phone)
  const payload: Record<string, string> = {}
  if (externalId) {
    payload.external_id = externalId
  }
  if (email) {
    payload.em = email
  }
  if (phone) {
    payload.ph = phone
  }
  return payload
}

const buildMetaPixelCommerceParams = (
  payload: CommerceEventPayload
): Record<string, unknown> => {
  const params: Record<string, unknown> = {}
  const contentIds = payload.items
    .map((item) => item.item_id)
    .filter((id): id is string => typeof id === "string" && id.length > 0)
  if (contentIds.length > 0) {
    params.content_ids = contentIds
  }
  const contents = payload.items
    .map((item) => {
      const entry: Record<string, unknown> = {}
      if (item.item_id) {
        entry.id = item.item_id
      }
      if (typeof item.quantity === "number" && Number.isFinite(item.quantity)) {
        entry.quantity = item.quantity
      }
      if (typeof item.price === "number" && Number.isFinite(item.price)) {
        entry.item_price = sanitizeAmount(item.price)
      }
      return entry
    })
    .filter((entry) => Object.keys(entry).length > 0)
  if (contents.length > 0) {
    params.contents = contents
  }
  const numItems = payload.items.reduce((total, item) => {
    const quantity =
      typeof item.quantity === "number" && Number.isFinite(item.quantity)
        ? item.quantity
        : 0
    return total + quantity
  }, 0)
  if (numItems > 0) {
    params.num_items = numItems
  }
  const value = sanitizeAmount(payload.value)
  if (typeof value === "number") {
    params.value = value
  }
  const currency = sanitizeCurrency(payload.currency)
  if (currency) {
    params.currency = currency
  }
  const firstNamedItem = payload.items.find((item) => Boolean(item.item_name))
  if (firstNamedItem?.item_name) {
    params.content_name = firstNamedItem.item_name
  }
  const firstCategorizedItem = payload.items.find((item) =>
    Boolean(item.item_category)
  )
  if (firstCategorizedItem?.item_category) {
    params.content_category = firstCategorizedItem.item_category
  }
  return params
}

export const mapCommerceEventToMetaPixel = (
  eventName: string,
  payload: CommerceEventPayload
): MetaPixelEvent | null => {
  const baseParams = buildMetaPixelCommerceParams(payload)

  switch (eventName) {
    case "view_item":
      return {
        method: "track",
        name: "ViewContent",
        params: {
          ...baseParams,
          content_type:
            payload.contentType === "product_group"
              ? "product_group"
              : "product",
        },
      }
    case "view_cart":
      return {
        method: "trackCustom",
        name: "ViewCart",
        params: {
          ...baseParams,
          content_type: "product_group",
          content_name:
            (baseParams.content_name as string | undefined) || "Cart",
        },
      }
    case "add_to_cart":
      return {
        method: "track",
        name: "AddToCart",
        params: { ...baseParams, content_type: "product" },
      }
    case "customize_product":
      return {
        method: "track",
        name: "CustomizeProduct",
        params: { ...baseParams, content_type: "product" },
      }
    case "remove_from_cart":
      return {
        method: "trackCustom",
        name: "RemoveFromCart",
        params: { ...baseParams, content_type: "product" },
      }
    case "begin_checkout":
      return {
        method: "track",
        name: "InitiateCheckout",
        params: { ...baseParams, content_type: "product" },
      }
    case "add_payment_info":
      return {
        method: "track",
        name: "AddPaymentInfo",
        params: {
          ...baseParams,
          content_type: "product",
          ...(payload.paymentMethod
            ? { payment_method: payload.paymentMethod }
            : {}),
        },
      }
    case "purchase": {
      const params: Record<string, unknown> = {
        ...baseParams,
        content_type: "product",
      }
      if (payload.transactionId) {
        params.order_id = payload.transactionId
      }
      if (payload.coupon) {
        params.coupon = payload.coupon
      }
      return {
        method: "track",
        name: "Purchase",
        params,
      }
    }
    case "checkout_exit":
      return {
        method: "trackCustom",
        name: "CheckoutExited",
        params: { ...baseParams, content_type: "product" },
      }
    default:
      return null
  }
}

export const mapSearchEventToMetaPixel = (
  searchQuery: string,
  catalogContentIds: string[] = []
): MetaPixelEvent | null => {
  const normalized = searchQuery.trim().replace(/\s+/g, " ").slice(0, 100)
  const contentIds = Array.from(
    new Set(
      catalogContentIds
        .filter((id): id is string => typeof id === "string")
        .map((id) => id.trim())
        .filter(Boolean)
    )
  ).slice(0, 100)
  return normalized.length >= 2
    ? {
        method: "track",
        name: "Search",
        params: {
          search_string: normalized,
          ...(contentIds.length
            ? {
                content_ids: contentIds,
                contents: contentIds.map((id) => ({ id, quantity: 1 })),
                content_type: "product",
              }
            : {}),
        },
      }
    : null
}

type AnalyticsContextValue = {
  measurementId?: string
  ready: boolean
  metaPixelActive: boolean
  metaCapiActive: boolean
  getMetaSignals: () => MetaBrowserSignals | null
  setMetaUserData: (userData?: MetaPixelUserData) => void
  setMarketingConsent: (consent: MetaMarketingConsent) => void
  trackEvent: (
    eventName: string,
    params?: Record<string, unknown>,
    meta?: AnalyticsEventMeta
  ) => void
  trackViewItem: (
    payload: CommerceEventPayload,
    meta?: AnalyticsEventMeta
  ) => void
  trackViewCart: (
    payload: CommerceEventPayload,
    meta?: AnalyticsEventMeta
  ) => void
  trackAddToCart: (
    payload: CommerceEventPayload,
    meta?: AnalyticsEventMeta
  ) => void
  trackCustomizeProduct: (
    payload: CommerceEventPayload,
    meta?: AnalyticsEventMeta
  ) => void
  trackRemoveFromCart: (
    payload: CommerceEventPayload,
    meta?: AnalyticsEventMeta
  ) => void
  trackBeginCheckout: (
    payload: CommerceEventPayload,
    meta?: AnalyticsEventMeta
  ) => void
  trackAddPaymentInfo: (
    payload: CommerceEventPayload,
    meta?: AnalyticsEventMeta
  ) => void
  trackSearch: (
    searchQuery: string,
    meta?: AnalyticsEventMeta,
    catalogContentIds?: string[]
  ) => void
  trackPurchase: (
    payload: CommerceEventPayload,
    meta?: AnalyticsEventMeta
  ) => void
  trackCheckoutExit: (
    payload: CommerceEventPayload,
    meta?: AnalyticsEventMeta
  ) => void
  trackCompleteRegistration: (
    params?: Record<string, unknown>,
    meta?: AnalyticsEventMeta
  ) => void
}

const noop = () => {}

const AnalyticsContext = createContext<AnalyticsContextValue>({
  ready: false,
  metaPixelActive: false,
  metaCapiActive: false,
  getMetaSignals: () => null,
  setMetaUserData: noop,
  setMarketingConsent: noop,
  trackEvent: noop,
  trackViewItem: noop,
  trackViewCart: noop,
  trackAddToCart: noop,
  trackCustomizeProduct: noop,
  trackRemoveFromCart: noop,
  trackBeginCheckout: noop,
  trackAddPaymentInfo: noop,
  trackSearch: noop,
  trackPurchase: noop,
  trackCheckoutExit: noop,
  trackCompleteRegistration: noop,
})

const ensureGtagBootstrap = () => {
  if (typeof window === "undefined") {
    return undefined
  }

  const analyticsWindow = window as AnalyticsWindow
  analyticsWindow.dataLayer = analyticsWindow.dataLayer || []
  if (typeof analyticsWindow.gtag !== "function") {
    analyticsWindow.gtag = function gtag(...args: unknown[]) {
      analyticsWindow.dataLayer?.push(args)
    }
  }

  return analyticsWindow.gtag
}

const ensureFbqBootstrap = () => {
  if (typeof window === "undefined") {
    return undefined
  }

  const analyticsWindow = window as AnalyticsWindow
  const existing = analyticsWindow.fbq
  if (typeof existing === "function") {
    return existing
  }

  const fbqStub = ((...args: unknown[]) => {
    if (typeof fbqStub.callMethod === "function") {
      fbqStub.callMethod(...args)
      return
    }
    fbqStub.queue?.push(args)
  }) as FbqFunction

  fbqStub.push = fbqStub
  fbqStub.loaded = false
  fbqStub.version = "2.0"
  fbqStub.queue = []

  analyticsWindow.fbq = fbqStub
  analyticsWindow._fbq = fbqStub

  return fbqStub
}

const sendGaEvent = (eventName: string, params?: Record<string, unknown>) => {
  if (typeof window === "undefined") {
    return
  }

  const gtag = ensureGtagBootstrap()
  if (typeof gtag !== "function") {
    return
  }

  try {
    gtag("event", eventName, params)
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("Analytics event failed", { eventName, error })
    }
  }
}

export const resolveMetaChannelReadiness = ({
  pixelId,
  pixelEnabled,
  capiEnabled,
  capiStatus,
  consent,
  consentPolicy,
}: {
  pixelId?: string | null
  pixelEnabled?: boolean | null
  capiEnabled?: boolean | null
  capiStatus?: "disabled" | "incomplete" | "ready" | null
  consent: MetaMarketingConsent
  consentPolicy: "allow_unknown" | "require_granted"
}) => {
  const hasPixelId = Boolean(sanitizeMetaPixelId(pixelId))
  const allowed = canUseMetaMarketing({
    consent,
    policy: consentPolicy,
  })
  const metaPixelActive = Boolean(hasPixelId && pixelEnabled && allowed)
  const metaCapiActive = Boolean(
    hasPixelId && capiEnabled && capiStatus === "ready" && allowed
  )
  return {
    metaPixelActive,
    metaCapiActive,
    metaAnyActive: metaPixelActive || metaCapiActive,
  }
}

export function AnalyticsProvider({
  measurementId,
  metaPixelId,
  metaPixelEnabled = false,
  metaCapiEnabled = false,
  metaCapiStatus = "disabled",
  metaMarketingConsentPolicy = "allow_unknown",
  children,
}: {
  measurementId?: string | null
  metaPixelId?: string | null
  metaPixelEnabled?: boolean | null
  metaCapiEnabled?: boolean | null
  metaCapiStatus?: "disabled" | "incomplete" | "ready" | null
  metaMarketingConsentPolicy?: "allow_unknown" | "require_granted" | null
  children: React.ReactNode
}) {
  const normalizedId =
    typeof measurementId === "string" && measurementId.trim()
      ? measurementId.trim()
      : undefined
  const pixelId = sanitizeMetaPixelId(metaPixelId)

  const gaMeasurementId = normalizedId
  const initializedPixelsRef = useRef<Record<string, true>>({})
  const pendingMetaEventsRef = useRef<MetaPixelEvent[]>([])
  const [marketingConsent, setMarketingConsentState] =
    useState<MetaMarketingConsent>("unknown")
  const [marketingConsentResolved, setMarketingConsentResolved] =
    useState(false)
  const setMarketingConsent = useCallback((consent: MetaMarketingConsent) => {
    setMetaMarketingConsent(consent)
    setMarketingConsentState(consent)
    setMarketingConsentResolved(true)
  }, [])
  const channelReadiness = resolveMetaChannelReadiness({
      pixelId,
      pixelEnabled: metaPixelEnabled,
      capiEnabled: metaCapiEnabled,
      capiStatus: metaCapiStatus,
      consent: marketingConsent,
      consentPolicy: metaMarketingConsentPolicy,
    })
  const metaPixelActive =
    marketingConsentResolved && channelReadiness.metaPixelActive
  const metaCapiActive =
    marketingConsentResolved && channelReadiness.metaCapiActive
  const metaAnyActive = metaPixelActive || metaCapiActive
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const searchString = searchParams?.toString() ?? ""
  const resolvedPath = useMemo(() => {
    const path = pathname || "/"
    return searchString ? `${path}?${searchString}` : path
  }, [pathname, searchString])
  const [metaUserData, setMetaUserDataState] = useState<MetaPixelUserData>({})
  const setMetaUserData = useCallback((userData?: MetaPixelUserData) => {
    setMetaUserDataState((previous) => {
      const nextExternalId =
        sanitizeMetaUserDataString(userData?.externalId) ||
        previous.externalId
      const nextEmail =
        sanitizeMetaUserDataEmail(userData?.email) || previous.email
      const nextPhone =
        sanitizeMetaUserDataPhone(userData?.phone) || previous.phone
      if (
        nextExternalId === previous.externalId &&
        nextEmail === previous.email &&
        nextPhone === previous.phone
      ) {
        return previous
      }
      return {
        ...(nextExternalId ? { externalId: nextExternalId } : {}),
        ...(nextEmail ? { email: nextEmail } : {}),
        ...(nextPhone ? { phone: nextPhone } : {}),
      }
    })
  }, [])
  const getMetaSignals = useCallback((): MetaBrowserSignals | null => {
    if (!metaAnyActive || typeof window === "undefined") return null
    return getMetaBrowserSignals({ allowIdentifiers: true })
  }, [metaAnyActive])
  const metaAdvancedMatchingData = useMemo(
    () => buildMetaPixelAdvancedMatchingData(metaUserData),
    [metaUserData]
  )

  useEffect(() => {
    const storedConsent = getMetaMarketingConsent()
    if (storedConsent === "denied") {
      clearMetaBrowserIdentifiers()
    }
    setMarketingConsentState(storedConsent)
    setMarketingConsentResolved(true)
  }, [])

  useEffect(() => {
    const handleConsent = (event: Event) => {
      const next = (event as CustomEvent<MetaMarketingConsent>).detail
      if (next === "granted" || next === "denied" || next === "unknown") {
        if (next === "denied") {
          clearMetaBrowserIdentifiers()
          setMetaUserDataState((previous) => ({
            ...(previous.email ? { email: previous.email } : {}),
            ...(previous.phone ? { phone: previous.phone } : {}),
          }))
        }
        setMarketingConsentState(next)
        setMarketingConsentResolved(true)
      }
    }
    window.addEventListener("meta:marketing-consent", handleConsent)
    return () => window.removeEventListener("meta:marketing-consent", handleConsent)
  }, [])

  const flushPendingMetaEvents = useCallback((fbq: FbqFunction) => {
    if (!pendingMetaEventsRef.current.length) {
      return
    }
    const queued = pendingMetaEventsRef.current
    pendingMetaEventsRef.current = []
    queued.forEach((event) => {
      const options = event.eventId ? { eventID: event.eventId } : undefined
      try {
        if (options) {
          fbq(event.method, event.name, event.params, options)
        } else {
          fbq(event.method, event.name, event.params)
        }
      } catch (error) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("Meta Pixel event failed", { event, error })
        }
      }
    })
  }, [])
  useEffect(() => {
    if (!metaAnyActive) return
    const signalExternalId = sanitizeMetaUserDataString(
      getMetaSignals()?.externalId
    )
    if (signalExternalId) {
      setMetaUserData({ externalId: signalExternalId })
    }
  }, [getMetaSignals, metaAnyActive, setMetaUserData])

  useEffect(() => {
    if (!metaAnyActive || typeof window === "undefined") return
    // Warm the official client builder once per meaningful full URL. The
    // adapter coalesces this with the PageView companion call below.
    void processMetaClientParams(window.location.href)
  }, [resolvedPath, metaAnyActive])

  useEffect(() => {
    if (
      marketingConsent !== "denied" ||
      !pixelId ||
      typeof window === "undefined"
    ) {
      return
    }
    const analyticsWindow = window as AnalyticsWindow
    const deniedPaths = (analyticsWindow.__metaDeniedPageViewPaths ||= {})
    deniedPaths[pixelId] = resolvedPath
  }, [marketingConsent, pixelId, resolvedPath])

  useEffect(() => {
    if (!gaMeasurementId || typeof window === "undefined") {
      return
    }

    const gtag = ensureGtagBootstrap()
    if (typeof gtag !== "function") {
      return
    }

    const analyticsWindow = window as AnalyticsWindow
    const configuredIds = analyticsWindow.__gaConfiguredIds || {}
    if (!analyticsWindow.__gaConfiguredIds) {
      analyticsWindow.__gaConfiguredIds = configuredIds
    }

    if (!configuredIds[gaMeasurementId]) {
      gtag("js", new Date())
      gtag("config", gaMeasurementId, {
        anonymize_ip: true,
        allow_google_signals: false,
      })
      configuredIds[gaMeasurementId] = true
    }
  }, [gaMeasurementId])

  useEffect(() => {
    if (!metaPixelActive) {
      pendingMetaEventsRef.current = []
      if (typeof window !== "undefined" && marketingConsent === "denied") {
        ;(window as AnalyticsWindow).fbq?.("consent", "revoke")
      }
      return
    }

    if (typeof window === "undefined") {
      return
    }

    const analyticsWindow = window as AnalyticsWindow
    if (!analyticsWindow.__metaPixelInitializedIds) {
      analyticsWindow.__metaPixelInitializedIds = initializedPixelsRef.current
    }

    const fbq = ensureFbqBootstrap()
    if (typeof fbq !== "function") {
      return
    }

    const initStore = analyticsWindow.__metaPixelInitializedIds
    fbq.disablePushState = true
    if (marketingConsent === "granted") {
      fbq("consent", "grant")
    }
    const hasMetaUserData = Object.keys(metaAdvancedMatchingData).length > 0
    initializeMetaPixelOnce({
      fbq,
      pixelId,
      userData: hasMetaUserData ? metaAdvancedMatchingData : undefined,
      initializedIds: initStore,
    })

    flushPendingMetaEvents(fbq)
  }, [
    flushPendingMetaEvents,
    metaAdvancedMatchingData,
    metaPixelActive,
    marketingConsent,
    pixelId,
  ])

  useEffect(() => {
    if (!pixelId || !metaAnyActive || typeof window === "undefined") return
    const analyticsWindow = window as AnalyticsWindow
    const deniedPaths = analyticsWindow.__metaDeniedPageViewPaths
    if (deniedPaths?.[pixelId] === resolvedPath) return
    if (deniedPaths?.[pixelId]) delete deniedPaths[pixelId]
    const lifecycleStore = (analyticsWindow.__metaPageViewLifecycles ||= {})
    const lifecycle = resolveMetaPageViewLifecycle({
      current: lifecycleStore[pixelId],
      path: resolvedPath,
      eventSourceUrl: window.location.href,
      documentReferrer: document.referrer,
      createEventId: createBrowserEventId,
    })
    lifecycleStore[pixelId] = lifecycle
    if (!claimMetaPageViewLifecycle(lifecycle)) return
    if (metaPixelActive) {
      const fbq = analyticsWindow.fbq || ensureFbqBootstrap()
      fbq?.("track", "PageView", {}, { eventID: lifecycle.eventId })
    }
    if (!metaCapiActive) return
    void processMetaClientParamsWithStatus(lifecycle.eventSourceUrl)
      .then(({ params: builderParams, status: clientBuilderStatus }) => {
        const signals = getMetaSignals()
        if (!signals || signals.consent === "denied") return
        return sendMetaAnalyticsCompanion({
          kind: "page-view",
          eventId: lifecycle.eventId,
          signals: {
            ...signals,
            ...builderParams,
            eventSourceUrl: lifecycle.eventSourceUrl,
            referrerUrl: lifecycle.referrerUrl,
          },
          clientBuilderStatus,
        })
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") {
          console.warn("Meta PageView companion failed", error)
        }
      })
  }, [
    resolvedPath,
    pixelId,
    metaAnyActive,
    metaPixelActive,
    metaCapiActive,
    getMetaSignals,
  ])

  useEffect(() => {
    if (!gaMeasurementId) {
      return
    }
    const gtag = ensureGtagBootstrap()
    if (typeof gtag !== "function") {
      return
    }
    gtag("config", gaMeasurementId, {
      page_path: resolvedPath,
    })
  }, [gaMeasurementId, resolvedPath])

  const value = useMemo<AnalyticsContextValue>(() => {
    if (!gaMeasurementId && !metaAnyActive) {
      return {
        measurementId: undefined,
        ready: false,
        metaPixelActive: false,
        metaCapiActive: false,
        getMetaSignals,
        setMetaUserData,
        setMarketingConsent,
        trackEvent: noop,
        trackViewItem: noop,
        trackViewCart: noop,
        trackAddToCart: noop,
        trackCustomizeProduct: noop,
        trackRemoveFromCart: noop,
        trackBeginCheckout: noop,
        trackAddPaymentInfo: noop,
        trackSearch: noop,
        trackPurchase: noop,
        trackCheckoutExit: noop,
        trackCompleteRegistration: noop,
      }
    }

    const trackGa = gaMeasurementId
      ? (eventName: string, params?: Record<string, unknown>) => {
          sendGaEvent(eventName, params)
        }
      : noop

    const trackMeta = metaPixelActive
      ? (
          method: MetaPixelEvent["method"],
          name: string,
          params?: Record<string, unknown>,
          meta?: AnalyticsEventMeta
        ) => {
          if (typeof window === "undefined") {
            return
          }
          const analyticsWindow = window as AnalyticsWindow
          const fbq = analyticsWindow.fbq || ensureFbqBootstrap()
          const eventId = sanitizeEventId(meta?.eventId)
          if (
            typeof fbq !== "function" ||
            !pixelId ||
            !analyticsWindow.__metaPixelInitializedIds?.[pixelId]
          ) {
            pendingMetaEventsRef.current.push({ method, name, params, eventId })
            return
          }
          try {
            if (eventId) {
              fbq(method, name, params, { eventID: eventId })
            } else {
              fbq(method, name, params)
            }
          } catch (error) {
            if (process.env.NODE_ENV !== "production") {
              console.warn("Meta Pixel event failed", { method, name, error })
            }
          }
        }
      : noop

    const trackEvent = (
      eventName: string,
      params?: Record<string, unknown>,
      meta?: AnalyticsEventMeta
    ) => {
      trackGa(eventName, params)
      if (metaPixelActive) {
        trackMeta("trackCustom", eventName, params, meta)
      }
    }

    const trackCommerceEvent = (
      eventName: string,
      payload: CommerceEventPayload,
      meta?: AnalyticsEventMeta
    ) => {
      const sanitized = {
        ...payload,
        value:
          typeof payload.value === "number"
            ? Number(payload.value.toFixed(2))
            : undefined,
        coupon: payload.coupon || undefined,
        transaction_id: payload.transactionId,
      } as Record<string, unknown>
      delete sanitized.transactionId

      trackGa(eventName, sanitized)

      if (metaPixelActive) {
        const mapped = mapCommerceEventToMetaPixel(eventName, payload)
        if (mapped) {
          trackMeta(mapped.method, mapped.name, mapped.params, meta)
        }
      }
    }

    const trackSearch = (
      searchQuery: string,
      meta?: AnalyticsEventMeta,
      catalogContentIds?: string[]
    ) => {
      const mapped = mapSearchEventToMetaPixel(searchQuery, catalogContentIds)
      const normalized = mapped?.params?.search_string
      if (typeof normalized !== "string") return
      trackGa("search", { search_term: normalized })
      if (metaPixelActive) {
        trackMeta(mapped.method, mapped.name, mapped.params, meta)
      }
    }

    return {
      measurementId: gaMeasurementId,
      ready: Boolean(gaMeasurementId) || metaAnyActive,
      metaPixelActive,
      metaCapiActive,
      getMetaSignals,
      setMetaUserData,
      setMarketingConsent,
      trackEvent,
      trackViewItem: (payload, meta) =>
        trackCommerceEvent("view_item", payload, meta),
      trackViewCart: (payload, meta) =>
        trackCommerceEvent("view_cart", payload, meta),
      trackAddToCart: (payload, meta) =>
        trackCommerceEvent("add_to_cart", payload, meta),
      trackCustomizeProduct: (payload, meta) =>
        trackCommerceEvent("customize_product", payload, meta),
      trackRemoveFromCart: (payload, meta) =>
        trackCommerceEvent("remove_from_cart", payload, meta),
      trackBeginCheckout: (payload, meta) =>
        trackCommerceEvent("begin_checkout", payload, meta),
      trackAddPaymentInfo: (payload, meta) =>
        trackCommerceEvent("add_payment_info", payload, meta),
      trackSearch,
      trackPurchase: (payload, meta) =>
        trackCommerceEvent("purchase", payload, meta),
      trackCheckoutExit: (payload, meta) =>
        trackCommerceEvent("checkout_exit", payload, meta),
      trackCompleteRegistration: (params, meta) => {
        trackGa("sign_up", params)
        if (metaPixelActive) {
          trackMeta("track", "CompleteRegistration", params || {}, meta)
        }
      },
    }
  }, [
    gaMeasurementId,
    metaAnyActive,
    metaPixelActive,
    metaCapiActive,
    pixelId,
    getMetaSignals,
    setMarketingConsent,
    setMetaUserData,
  ])

  return (
    <AnalyticsContext.Provider value={value}>
      {gaMeasurementId ? (
        <Script
          id="ga-loader"
          strategy="lazyOnload"
          src={`https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`}
        />
      ) : null}
      {metaPixelActive ? (
        <Script
          id="fb-pixel-loader"
          strategy="afterInteractive"
          src="https://connect.facebook.net/en_US/fbevents.js"
        />
      ) : null}
      {children}
    </AnalyticsContext.Provider>
  )
}

export function useAnalytics() {
  return useContext(AnalyticsContext)
}

export type {
  AnalyticsItem,
  CommerceEventPayload,
  AnalyticsEventMeta,
  MetaPixelUserData,
}
